import type React from 'react';
import type { ForwardedRef, KeyboardEvent, PropsWithChildren, SyntheticEvent } from 'react';
import { forwardRef, useMemo } from 'react';
import { useKeyboardActivation } from '../../hooks/useKeyboardActivation';
import { slowCn } from '../../lib/utils';
import { isNativeInteractiveElement } from '../../utils/isNativeInteractiveElement';
import { stackStyle } from './styles';
import type { BaseStackProps } from './types';

function getParsedGap({ gap }: { gap: string | number | undefined }) {
  if (typeof gap === 'number') return `${(gap * 8).toString()}px`;
  if (typeof gap === 'string') return gap;
  return undefined;
}
function getParsedSpacing({ spacing }: { spacing: string | number | undefined }) {
  if (typeof spacing === 'number') return `${(spacing * 8).toString()}px`;
  if (typeof spacing === 'string') return spacing;
  return undefined;
}
function StackComponent<T>(
  {
    children,
    spacing,
    gap,
    className,
    component: Component = 'div',
    direction = 'column',
    alignItems,
    justifyContent = 'flex-start',
    flexWrap,
    alignContent,
    justifyItems,
    style,
    enableKeyboardActivation,
    onClick,
    onKeyDown: onKeyDownProp,
    tabIndex: tabIndexProp,
    ...rest
  }: PropsWithChildren<BaseStackProps>,
  ref: ForwardedRef<T>,
) {
  const _className = useMemo(
    () =>
      stackStyle({
        direction,
        alignItems,
        justifyContent,
        flexWrap,
        alignContent,
        justifyItems,
      }),
    [direction, alignItems, justifyContent, flexWrap, alignContent, justifyItems],
  );

  const mergedStyles = useMemo(() => {
    const parsedGap = getParsedGap({ gap });
    const parsedSpacing = getParsedSpacing({ spacing });

    return {
      gap: parsedGap || parsedSpacing,
      ...(style || {}),
    };
  }, [gap, spacing, style]);

  const isKeyboardActivationRequested = Boolean(enableKeyboardActivation);
  const hasClickHandler = typeof onClick === 'function';
  const isNativeInteractive = isNativeInteractiveElement(Component);
  const keyboardActivationEnabled =
    isKeyboardActivationRequested && hasClickHandler && !isNativeInteractive;

  const { tabIndex, onKeyDown } = useKeyboardActivation({
    hasClickEvent: keyboardActivationEnabled,
    onClick: onClick as ((event: SyntheticEvent) => void) | undefined,
  });

  const composedOnKeyDown = useMemo(() => {
    if (onKeyDown && onKeyDownProp) {
      return (event: KeyboardEvent<HTMLDivElement>) => {
        onKeyDownProp(event);
        if (!event.defaultPrevented) onKeyDown(event);
      };
    }
    return onKeyDown ?? onKeyDownProp;
  }, [onKeyDown, onKeyDownProp]);

  return (
    <Component
      className={slowCn(_className, className)}
      onClick={onClick}
      onKeyDown={composedOnKeyDown}
      ref={ref}
      tabIndex={tabIndex ?? tabIndexProp}
      {...rest}
      style={mergedStyles}
    >
      {children}
    </Component>
  );
}

const StackForwardRef = forwardRef(StackComponent);
StackForwardRef.displayName = 'Stack';

export const Stack = StackForwardRef as <T>(
  props: PropsWithChildren<BaseStackProps> & { ref?: ForwardedRef<T> },
) => React.ReactElement;

export default Stack;
